import { PortalAdminAdvancedPortalDataPageName, PortalAdminPageName } from '@pages/PortalAdmin/PortalAdmin.constants';
import { IRoute } from '@router/routes/Route.interface';
import { PortalAdminPages } from '@pages/PortalAdmin/PortalAdmin.constants';
import { lazy } from 'react';
import { UserRoles } from '@models/user';

const BrokerageManagerPage = lazy(() => import('../../pages/PortalAdmin/BrokerageManager/BrokerageManagerPage'));
const DailyCatPage = lazy(() => import('../../pages/PortalAdmin/DailyCatPage'));

const GlobalAlertManagementPage = lazy(
  () => import('../../pages/PortalAdmin/GlobalAlertManagement/GlobalAlertManagementPage'),
);
const AdvancedPortalDataPage = lazy(() => import('../../pages/PortalAdmin/AdvancedPortalData/AdvancedPortalDataPage'));

const BrokerageCommissionView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/BrokerageCommissionView/BrokerageCommissionView'),
);

const BusinessClassesView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/BusinessClassesView/BusinessClassesView'),
);

const OfferingDefaultJurisdictionView = lazy(
  () =>
    import('../../pages/PortalAdmin/AdvancedPortalData/OfferingDefaultJurisdictionView/OfferingDefaultJurisdictionView'),
);

const OfferingSubjectivitiesView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/OfferingSubjectivitiesView/OfferingSubjectivitiesView'),
);

const CoverageView = lazy(() => import('../../pages/PortalAdmin/AdvancedPortalData/CoverageView/CoverageView'));

const PostRateDefaultFactorsView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/PostRateDefaultFactorsView/PostRateDefaultFactorsView'),
);

const LOIAvailabilityView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/LOIAvailabilityView/LOIAvailabilityView'),
);

const LegalNoticeManagementView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/LegalNoticeManagementView/LegalNoticeManagementView'),
);

const CustomActivityTypesView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/CustomActivityTypesView/CustomActivityTypesView'),
);
const MaintenanceModePage = lazy(() => import('../../pages/PortalAdmin/MaintenanceMode/MaintenanceModePage'));

const OfferingManagementPage = lazy(() => import('../../pages/PortalAdmin/OfferingManagement/OfferingManagementPage'));

const PortalAdminLayout = lazy(() =>
  import('../../pages/PortalAdmin/PortalAdminLayout').then((module) => ({ default: module.PortalAdminLayout })),
);

const QuoteSetupPresetManagementPage = lazy(
  () => import('../../pages/PortalAdmin/QuoteSetupPresetManagement/QuoteSetupPresetManagementPage'),
);

const MoratoriumManagementPage = lazy(
  () => import('../../pages/PortalAdmin/MoratoriumManagement/MoratoriumManagementPage'),
);

const UnderwriterManagementPage = lazy(
  () => import('../../pages/PortalAdmin/UnderwriterManagement/UnderwriterManagementPage'),
);

const UserManagementPage = lazy(() => import('../../pages/PortalAdmin/UserManagement/UserManagementPage'));
const FormsV2AdminPage = lazy(() => import('../../pages/PortalAdmin/FormsV2Admin/FormsV2AdminPage'));
const SubmissionDraftsPage = lazy(() => import('../../pages/PortalAdmin/SubmissionDrafts/SubmissionDraftsPage'));
const UiPlaygroundPage = lazy(() => import('../../pages/PortalAdmin/UIPlayground/UIPlaygroundPage'));
const BORTransferRequestsPage = lazy(
  () => import('../../pages/PortalAdmin/BORTransferRequests/BORTransferRequestsPage'),
);
const ApiClientManagementPage = lazy(
  () => import('../../pages/PortalAdmin/ApiClientManagement/ApiClientManagementPage'),
);

export const adminRoutes: IRoute[] = [
  {
    roles: [
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
      UserRoles.Producer,
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.LioSales,
      UserRoles.JrOps,
    ],
    path: 'portal-admin',
    indexPath: 'brokerage-management',
    component: <PortalAdminLayout />,
    outlets: [
      {
        component: <BrokerageManagerPage />,
        roles: PortalAdminPages[PortalAdminPageName.Brokerage].roles,
      },
      {
        component: <BrokerageManagerPage />,
        path: PortalAdminPages[PortalAdminPageName.Brokerage].url,
        roles: PortalAdminPages[PortalAdminPageName.Brokerage].roles,
      },
      {
        component: <UserManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.User].url,
        roles: PortalAdminPages[PortalAdminPageName.User].roles,
      },
      {
        component: <OfferingManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.Offering].url,
        roles: PortalAdminPages[PortalAdminPageName.Offering].roles,
      },
      {
        component: <UnderwriterManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.UWMgmt].url,
        roles: PortalAdminPages[PortalAdminPageName.UWMgmt].roles,
      },
      {
        component: <MoratoriumManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.MoratoriumManagement].url,
        roles: PortalAdminPages[PortalAdminPageName.MoratoriumManagement].roles,
      },
      {
        component: <QuoteSetupPresetManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.QuoteSetupPresetManagement].url,
        roles: PortalAdminPages[PortalAdminPageName.QuoteSetupPresetManagement].roles,
      },
      {
        component: <AdvancedPortalDataPage />,
        path: PortalAdminPages[PortalAdminPageName.AdvancedPortalData].url,
        roles: PortalAdminPages[PortalAdminPageName.AdvancedPortalData].roles,
        indexPath: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.LOIAvailability].url,
        outlets: [
          {
            component: <LOIAvailabilityView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.LOIAvailability].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.LOIAvailability].roles,
          },
          {
            component: <BrokerageCommissionView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.BrokerageCommission].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.BrokerageCommission].roles,
          },
          {
            component: <BusinessClassesView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.BusinessClasses].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.BusinessClasses].roles,
          },
          {
            component: <OfferingDefaultJurisdictionView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.OfferingDefaultJurisdiction].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.OfferingDefaultJurisdiction].roles,
          },
          {
            component: <OfferingSubjectivitiesView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.OfferingSubjectivities].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.OfferingSubjectivities].roles,
          },
          {
            component: <CoverageView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.Coverages].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.Coverages].roles,
          },
          {
            component: <PostRateDefaultFactorsView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.PostRateDefaultFactors].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.PostRateDefaultFactors].roles,
          },
          {
            component: <LegalNoticeManagementView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.LegalNoticeManagement].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.LegalNoticeManagement].roles,
          },
          {
            component: <CustomActivityTypesView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.UnderwritingActivityTypes].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.UnderwritingActivityTypes].roles,
          },
        ],
      },
      {
        component: <MaintenanceModePage />,
        path: PortalAdminPages[PortalAdminPageName.Maintenance].url,
        roles: PortalAdminPages[PortalAdminPageName.Maintenance].roles,
      },
      {
        component: <GlobalAlertManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.Alerts].url,
        roles: PortalAdminPages[PortalAdminPageName.Alerts].roles,
      },
      {
        component: <DailyCatPage />,
        path: PortalAdminPages[PortalAdminPageName.CatMgmt].url,
        roles: PortalAdminPages[PortalAdminPageName.CatMgmt].roles,
      },
      {
        component: <FormsV2AdminPage />,
        path: PortalAdminPages[PortalAdminPageName.FormsV2Admin].url,
        roles: PortalAdminPages[PortalAdminPageName.FormsV2Admin].roles,
      },
      {
        component: <SubmissionDraftsPage />,
        path: PortalAdminPages[PortalAdminPageName.SubmissionDrafts].url,
        roles: PortalAdminPages[PortalAdminPageName.SubmissionDrafts].roles,
      },
      {
        component: <UiPlaygroundPage />,
        path: PortalAdminPages[PortalAdminPageName.UiPlayground].url,
        roles: PortalAdminPages[PortalAdminPageName.UiPlayground].roles,
      },
      {
        component: <BORTransferRequestsPage />,
        path: PortalAdminPages[PortalAdminPageName.BORTransfer].url,
        roles: PortalAdminPages[PortalAdminPageName.BORTransfer].roles,
      },
      {
        component: <ApiClientManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.ApiClientManagement].url,
        roles: PortalAdminPages[PortalAdminPageName.ApiClientManagement].roles,
      },
    ],
  },
];
